import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { NewspaperIcon, PlayIcon, BookOpenIcon, SearchIcon, FilterIcon, CalendarIcon, UsersIcon, TagIcon, ArrowRightIcon, ChevronRightIcon, DownloadIcon, ExternalLinkIcon, CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const MediaInsightsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const topics = ['All Topics', 'Microbiome', 'Gut Health', 'Probiotics', 'Nutrition', 'Supplements', 'Research Methods', 'Clinical Trials', 'Industry Standards'];
  // Featured content
  const featuredContent = [{
    id: 1,
    title: 'The Future of Microbiome Research: 2025 Outlook',
    type: 'Article',
    date: 'January 15, 2024',
    author: 'Dr. Maria Chen',
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    excerpt: 'An in-depth analysis of emerging trends and breakthrough technologies in microbiome science that will shape the next decade of research and therapeutics.',
    topics: ['Microbiome', 'Research Methods'],
    link: '#article-future'
  }, {
    id: 2,
    title: 'Ukweli Talks: Probiotics Beyond the Hype',
    type: 'Video',
    date: 'December 8, 2023',
    author: 'Dr. James Wilson & Dr. Lisa Park',
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    excerpt: 'Our most-watched webinar featuring leading experts discussing what the science really says about probiotics and their impact on health outcomes.',
    topics: ['Probiotics', 'Gut Health'],
    duration: '48:22',
    link: '#video-probiotics'
  }, {
    id: 3,
    title: 'Evidence-Based Guide to Evaluating Supplement Claims',
    type: 'Guide',
    date: 'November 20, 2023',
    author: 'Ukweli Research Team',
    image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    excerpt: 'A comprehensive framework for consumers and practitioners to critically assess health claims on supplement products.',
    topics: ['Supplements', 'Industry Standards'],
    link: '#guide-claims'
  }];
  // All content items
  const allContent = [...featuredContent, {
    id: 4,
    title: 'The Truth About Probiotics: Separating Science from Marketing',
    type: 'Article',
    date: 'August 15, 2023',
    author: 'Dr. Sarah Chen',
    image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'An evidence-based analysis of common probiotic claims and what the research actually supports.',
    topics: ['Probiotics', 'Supplements'],
    link: '/article/probiotics-truth'
  }, {
    id: 5,
    title: 'Microbiome Research Explained: Interview with Dr. Sarah Chen',
    type: 'Video',
    date: 'July 28, 2023',
    author: 'Ukweli Media Team',
    image: 'https://images.unsplash.com/photo-1551817958-d9d86fb29431?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Leading researcher Dr. Sarah Chen breaks down complex microbiome concepts into accessible insights.',
    topics: ['Microbiome', 'Research Methods'],
    duration: '36:45',
    link: '/video/microbiome-explained'
  }, {
    id: 6,
    title: 'How Diet Affects Your Microbiome: New Research Findings',
    type: 'Article',
    date: 'June 12, 2023',
    author: 'Dr. Michael Rodriguez',
    image: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Recent studies reveal surprising connections between specific dietary patterns and microbiome diversity.',
    topics: ['Nutrition', 'Microbiome'],
    link: '/article/diet-microbiome'
  }, {
    id: 7,
    title: "Ukweli Institute's Guide to Microbiome Testing",
    type: 'Guide',
    date: 'May 30, 2023',
    author: 'Ukweli Research Team',
    image: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'A comprehensive overview of current microbiome testing methods, their reliability, and how to interpret results.',
    topics: ['Microbiome', 'Research Methods'],
    link: '/guide/microbiome-testing'
  }, {
    id: 8,
    title: 'The Microbiome-Gut-Brain Axis: Emerging Science',
    type: 'Podcast',
    date: 'April 18, 2023',
    author: 'Dr. Emily Johnson & Dr. David Kim',
    image: 'https://images.unsplash.com/photo-1589903308904-1010c2294adc?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Exploring the fascinating bidirectional communication between gut microbes and the brain.',
    topics: ['Microbiome', 'Gut Health'],
    duration: '52:18',
    link: '/podcast/gut-brain-axis'
  }, {
    id: 9,
    title: 'Certification Standards in the Supplement Industry',
    type: 'Whitepaper',
    date: 'March 5, 2023',
    author: 'Ukweli Regulatory Team',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'An analysis of current regulatory frameworks and the need for stronger evidence-based standards.',
    topics: ['Supplements', 'Industry Standards'],
    link: '/whitepaper/certification-standards'
  }, {
    id: 10,
    title: 'Probiotic Strains: A Clinical Comparison',
    type: 'Research Summary',
    date: 'February 12, 2023',
    author: 'Dr. Robert Chang',
    image: 'https://images.unsplash.com/photo-1581093458791-9a9cd7093ae3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Comparing the clinical evidence behind the most common probiotic strains in commercial products.',
    topics: ['Probiotics', 'Clinical Trials'],
    link: '/research/probiotic-strains'
  }, {
    id: 11,
    title: 'Ukweli Insights: The Science of Fermented Foods',
    type: 'Video',
    date: 'January 25, 2023',
    author: 'Dr. Lisa Park',
    image: 'https://images.unsplash.com/photo-1563355846-0e8ad5491037?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Exploring the microbiological processes and health benefits of traditional fermented foods.',
    topics: ['Nutrition', 'Microbiome'],
    duration: '28:42',
    link: '/video/fermented-foods'
  }, {
    id: 12,
    title: 'Consumer Guide: Reading Supplement Labels',
    type: 'Guide',
    date: 'December 10, 2022',
    author: 'Ukweli Education Team',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'A practical guide to understanding supplement facts panels and identifying quality products.',
    topics: ['Supplements', 'Industry Standards'],
    link: '/guide/supplement-labels'
  }];
  const getTypeIcon = type => {
    switch (type) {
      case 'Article':
        return <NewspaperIcon size={18} className="text-blue-600" data-id="element-893" />;
      case 'Video':
        return <PlayIcon size={18} className="text-blue-600" data-id="element-894" />;
      case 'Guide':
        return <BookOpenIcon size={18} className="text-blue-600" data-id="element-895" />;
      case 'Podcast':
        return <div size={18} className="text-blue-600" data-id="element-896" />;
      case 'Whitepaper':
        return <BookOpenIcon size={18} className="text-blue-600" data-id="element-897" />;
      case 'Research Summary':
        return <NewspaperIcon size={18} className="text-blue-600" data-id="element-898" />;
      default:
        return <NewspaperIcon size={18} className="text-blue-600" data-id="element-899" />;
    }
  };
  // Filter content based on active tab, search query, and selected topic
  const filteredContent = allContent.filter(item => {
    const matchesTab = activeTab === 'all' || item.type.toLowerCase() === activeTab.toLowerCase();
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTopic = selectedTopic === 'All Topics' || item.topics.includes(selectedTopic);
    return matchesTab && matchesSearch && matchesTopic;
  });
  return <div className="bg-white w-full" data-id="element-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-901">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-902">
          <div className="text-center" data-id="element-903">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-904">
              Media & Insights
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-905">
              Evidence-based articles, videos, and resources to help you
              navigate the complex world of microbiome science
            </p>
          </div>
        </div>
      </section>
      {/* Featured Content */}
      <section className="py-16 bg-white" data-id="element-906">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-907">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="mb-16" data-id="element-908">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-909">
              Featured Content
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-910">
              {featuredContent.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col h-full" data-id="element-911">
                  <div className="relative h-48" data-id="element-912">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-913" />
                    {item.type === 'Video' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-914">
                        <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-915">
                          <PlayIcon size={24} data-id="element-916" />
                        </div>
                      </div>}
                    <div className="absolute top-4 left-4" data-id="element-917">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-918">
                        {getTypeIcon(item.type)}
                        <span className="ml-1" data-id="element-919">{item.type}</span>
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-920">
                    <div className="flex items-center text-sm text-gray-500 mb-3" data-id="element-921">
                      <CalendarIcon size={14} className="mr-1" data-id="element-922" />
                      <span data-id="element-923">{item.date}</span>
                      {item.duration && <>
                          <span className="mx-2" data-id="element-924">•</span>
                          <span data-id="element-925">{item.duration}</span>
                        </>}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight" data-id="element-926">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-4 flex-grow" data-id="element-927">
                      {item.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-928">
                      {item.topics.map(topic => <span key={topic} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-929">
                          <TagIcon size={12} className="mr-1" data-id="element-930" />
                          {topic}
                        </span>)}
                    </div>
                    <div className="mt-auto" data-id="element-931">
                      <Link to={item.link} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-932">
                        Read more{' '}
                        <ChevronRightIcon size={16} className="ml-1" data-id="element-933" />
                      </Link>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Content Library */}
      <section className="py-16 bg-gray-50" data-id="element-934">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-935">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-936">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-937">
              Content Library
            </h2>
            {/* Search and Filter */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8" data-id="element-938">
              <div className="flex flex-col md:flex-row gap-4" data-id="element-939">
                <div className="flex-grow" data-id="element-940">
                  <div className="relative" data-id="element-941">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-942">
                      <SearchIcon size={18} className="text-gray-400" data-id="element-943" />
                    </div>
                    <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by title or keyword" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-944" />
                  </div>
                </div>
                <div className="w-full md:w-64" data-id="element-945">
                  <div className="relative" data-id="element-946">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-947">
                      <FilterIcon size={18} className="text-gray-400" data-id="element-948" />
                    </div>
                    <select className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)} data-id="element-949">
                      {topics.map(topic => <option key={topic} value={topic} data-id="element-950">
                          {topic}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>
              {/* Content Type Tabs */}
              <div className="mt-6 border-b border-gray-200" data-id="element-951">
                <nav className="-mb-px flex space-x-8" data-id="element-952">
                  <button onClick={() => setActiveTab('all')} className={`${activeTab === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`} data-id="element-953">
                    All
                  </button>
                  <button onClick={() => setActiveTab('article')} className={`${activeTab === 'article' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-954">
                    <NewspaperIcon size={16} className="mr-2" data-id="element-955" />
                    Articles
                  </button>
                  <button onClick={() => setActiveTab('video')} className={`${activeTab === 'video' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-956">
                    <PlayIcon size={16} className="mr-2" data-id="element-957" />
                    Videos
                  </button>
                  <button onClick={() => setActiveTab('guide')} className={`${activeTab === 'guide' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-958">
                    <BookOpenIcon size={16} className="mr-2" data-id="element-959" />
                    Guides
                  </button>
                  <button onClick={() => setActiveTab('podcast')} className={`${activeTab === 'podcast' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-960">
                    <div size={16} className="mr-2" data-id="element-961" />
                    Podcasts
                  </button>
                </nav>
              </div>
            </div>
            {/* Content List */}
            {filteredContent.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-962">
                {filteredContent.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index % 2 * 0.1
            }} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col md:flex-row" data-id="element-963">
                    <div className="md:w-2/5 h-48 md:h-auto relative" data-id="element-964">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" onError={e => {
                  e.target.src = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80';
                }} data-id="element-965" />
                      {item.type === 'Video' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-966">
                          <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-967">
                            <PlayIcon size={24} data-id="element-968" />
                          </div>
                        </div>}
                      {item.type === 'Podcast' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-969">
                          <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-970">
                            <BookOpenIcon size={24} data-id="element-971" />
                          </div>
                        </div>}
                    </div>
                    <div className="p-6 md:w-3/5 flex flex-col justify-between" data-id="element-972">
                      <div data-id="element-973">
                        <div className="flex items-center mb-2" data-id="element-974">
                          {getTypeIcon(item.type)}
                          <span className="ml-1 text-sm font-semibold text-blue-600" data-id="element-975">
                            {item.type}
                          </span>
                          <span className="mx-2 text-gray-400" data-id="element-976">•</span>
                          <span className="text-sm text-gray-500" data-id="element-977">
                            {item.date}
                          </span>
                          {item.duration && <>
                              <span className="mx-2 text-gray-400" data-id="element-978">•</span>
                              <span className="text-sm text-gray-500" data-id="element-979">
                                {item.duration}
                              </span>
                            </>}
                        </div>
                        <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-980">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-3" data-id="element-981">
                          {item.excerpt}
                        </p>
                        {item.author && <p className="text-sm text-gray-700 mb-3" data-id="element-982">
                            By:{' '}
                            {item.author === 'Dr. Sarah Chen' ? <Link to="/profile" className="text-blue-600 hover:text-blue-800" data-id="element-983">
                                {item.author}
                              </Link> : item.author}
                          </p>}
                        <div className="flex flex-wrap gap-2 mb-2" data-id="element-984">
                          {item.topics.map(topic => <span key={topic} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-985">
                              {topic}
                            </span>)}
                        </div>
                      </div>
                      <Link to={item.link} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium mt-2 text-sm" data-id="element-986">
                        Read more <ArrowRightIcon size={16} className="ml-1" data-id="element-987" />
                      </Link>
                    </div>
                  </motion.div>)}
              </div> : <div className="text-center py-12 bg-white rounded-lg shadow-md" data-id="element-988">
                <SearchIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-989" />
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-990">
                  No results found
                </h3>
                <p className="text-gray-600" data-id="element-991">
                  Try adjusting your search or filters
                </p>
              </div>}
            {/* Pagination - would be implemented with actual pagination logic */}
            {filteredContent.length > 0 && <div className="mt-12 flex justify-center" data-id="element-992">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-993">
                  <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-994">
                    <span className="sr-only" data-id="element-995">Previous</span>
                    <ChevronRightIcon size={16} className="transform rotate-180" data-id="element-996" />
                  </a>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-997">
                    1
                  </a>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-blue-50 text-sm font-medium text-blue-600 hover:bg-blue-100" data-id="element-998">
                    2
                  </a>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-999">
                    3
                  </a>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700" data-id="element-1000">
                    ...
                  </span>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1001">
                    8
                  </a>
                  <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-1002">
                    <span className="sr-only" data-id="element-1003">Next</span>
                    <ChevronRightIcon size={16} data-id="element-1004" />
                  </a>
                </nav>
              </div>}
          </motion.div>
        </div>
      </section>
      {/* Resources Section */}
      <section className="py-16 bg-white" data-id="element-1005">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1006">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1007">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1008">
              Educational Resources
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1009">
              <div className="bg-blue-50 rounded-lg p-8 shadow-md" data-id="element-1010">
                <div className="flex items-center mb-4" data-id="element-1011">
                  <DownloadIcon size={24} className="text-blue-600 mr-3" data-id="element-1012" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1013">
                    Downloadable Resources
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-1014">
                  Access our collection of free educational materials including
                  infographics, fact sheets, and research summaries.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-1015">
                  <li className="flex items-start" data-id="element-1016">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1017" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1018">
                      Probiotic Strains Comparison Chart
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1019">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1020" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1021">
                      Supplement Evaluation Checklist
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1022">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1023" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1024">
                      Microbiome Testing Guide
                    </a>
                  </li>
                </ul>
                <a href="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1025">
                  View all resources{' '}
                  <ArrowRightIcon size={16} className="ml-1" data-id="element-1026" />
                </a>
              </div>
              <div className="bg-blue-50 rounded-lg p-8 shadow-md" data-id="element-1027">
                <div className="flex items-center mb-4" data-id="element-1028">
                  <UsersIcon size={24} className="text-blue-600 mr-3" data-id="element-1029" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1030">
                    Webinars & Events
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-1031">
                  Join our live webinars and virtual events featuring expert
                  discussions on the latest microbiome research.
                </p>
                <div className="space-y-4 mb-6" data-id="element-1032">
                  <div className="bg-white p-4 rounded-md" data-id="element-1033">
                    <p className="text-sm text-blue-600 font-semibold" data-id="element-1034">
                      UPCOMING
                    </p>
                    <h4 className="font-bold mb-1" data-id="element-1035">
                      Microbiome & Immune Health
                    </h4>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1036">
                      May 15, 2024 • 2:00 PM EST
                    </p>
                    <a href="#" className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1037">
                      Register now
                    </a>
                  </div>
                  <div className="bg-white p-4 rounded-md" data-id="element-1038">
                    <p className="text-sm text-blue-600 font-semibold" data-id="element-1039">
                      UPCOMING
                    </p>
                    <h4 className="font-bold mb-1" data-id="element-1040">
                      Supplement Industry Standards Panel
                    </h4>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1041">
                      June 8, 2024 • 1:00 PM EST
                    </p>
                    <a href="#" className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1042">
                      Register now
                    </a>
                  </div>
                </div>
                <a href="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1043">
                  View all events <ArrowRightIcon size={16} className="ml-1" data-id="element-1044" />
                </a>
              </div>
              <div className="bg-blue-50 rounded-lg p-8 shadow-md" data-id="element-1045">
                <div className="flex items-center mb-4" data-id="element-1046">
                  <ExternalLinkIcon size={24} className="text-blue-600 mr-3" data-id="element-1047" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1048">
                    For Professionals
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-1049">
                  Specialized resources for healthcare providers, researchers,
                  and industry professionals.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-1050">
                  <li className="flex items-start" data-id="element-1051">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1052" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1053">
                      Clinical Application Guidelines
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1054">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1055" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1056">
                      Research Collaboration Opportunities
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1057">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1058" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1059">
                      Certification Program Details
                    </a>
                  </li>
                </ul>
                <a href="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1060">
                  Access professional resources{' '}
                  <ArrowRightIcon size={16} className="ml-1" data-id="element-1061" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Newsletter Signup */}
      <section className="py-16 bg-gray-50" data-id="element-1062">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1063">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-1064">
            <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1065">
              Stay Informed
            </h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-1066">
              Subscribe to our newsletter for the latest research insights,
              educational resources, and event announcements
            </p>
            <div className="max-w-lg mx-auto" data-id="element-1067">
              <form className="sm:flex" data-id="element-1068">
                <label htmlFor="email-address" className="sr-only" data-id="element-1069">
                  Email address
                </label>
                <input id="email-address" name="email" type="email" autoComplete="email" required className="w-full px-5 py-3 placeholder-gray-500 focus:ring-white focus:border-white sm:max-w-xs border-white rounded-md" placeholder="Enter your email" data-id="element-1070" />
                <div className="mt-3 rounded-md shadow sm:mt-0 sm:ml-3 sm:flex-shrink-0" data-id="element-1071">
                  <button type="submit" className="w-full flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-600 focus:ring-white" data-id="element-1072">
                    Subscribe
                  </button>
                </div>
              </form>
              <p className="mt-3 text-sm text-blue-100" data-id="element-1073">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
            <div className="mt-10 flex justify-center space-x-6" data-id="element-1074">
              <a href="#" className="text-white hover:text-blue-100" data-id="element-1075">
                <span className="sr-only" data-id="element-1076">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1077">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" data-id="element-1078" />
                </svg>
              </a>
              <a href="#" className="text-white hover:text-blue-100" data-id="element-1079">
                <span className="sr-only" data-id="element-1080">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1081">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" data-id="element-1082" />
                </svg>
              </a>
              <a href="#" className="text-white hover:text-blue-100" data-id="element-1083">
                <span className="sr-only" data-id="element-1084">YouTube</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1085">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-1086" />
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};